// Iter47-B — Admin → Tenants & Supply-Chain Relationships.
// Three vertical sections:
//   1. Tenant list (CRUD) with inline contacts editor
//   2. Relationships table (from → to + contacts + parts)
//   3. Parts table (part_number + owning_tenant + responsible_contacts)
import React, { useMemo, useState } from "react";
import { Building2, Plus, Edit2, Trash2, X, Check, Mail, Phone, Users as UsersIcon, ArrowRight, Package } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useStore } from "../store";
import { useT } from "../lib/i18n";
import {
  useTenants, useCreateTenant, useUpdateTenant, useDeleteTenant,
  useAddTenantContact, useDeleteTenantContact,
  useRelationships, useCreateRelationship, useDeleteRelationship,
  useTenantTypes, useUpsertPart, useDeletePart, useUpdatePart,
} from "../api/sitroom";
import { mono, inputStyle, Label, pickName } from "../components/MasterDataAtoms";

const RELATIONSHIP_TYPES = ["supplies-to", "sources-from", "transports-for", "stores-for", "services"];
const TABS = [
  { key: "tenants", label: "Tenants", icon: Building2 },
  { key: "relationships", label: "Supply-Chain Relationships", icon: ArrowRight },
  { key: "parts", label: "Parts & Responsibilities", icon: Package },
];

export default function AdminTenants() {
  const { user } = useAuth();
  const isPlatform = user?.role === "platform_admin";
  const [tab, setTab] = useState("tenants");

  return (
    <div data-testid="admin-tenants-page">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {TABS.map((t) => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
            style={{
              background: tab === t.key ? "rgba(24,95,165,0.18)" : "transparent",
              border: `1px solid ${tab === t.key ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.10)"}`,
              color: tab === t.key ? "#9ec3ec" : "var(--text-dim, #c5cad8)",
              ...mono(10, undefined, 700, "0.6px"), textTransform: "uppercase", cursor: "pointer",
            }}
            data-testid={`admin-tenants-tab-${t.key}`}>
            <t.icon size={12} /> {t.label}
          </button>
        ))}
      </div>
      {tab === "tenants" && <TenantsTab isPlatform={isPlatform} />}
      {tab === "relationships" && <RelationshipsTab isPlatform={isPlatform} />}
      {tab === "parts" && <PartsTab isPlatform={isPlatform} />}
    </div>
  );
}

// =================================================================
// Tab 1 — Tenants
// =================================================================
const TenantsTab = ({ isPlatform }) => {
  const { locale } = useT();
  const addToast = useStore((s) => s.addToast);
  const { data: tenants = [] } = useTenants();
  const { data: tenantTypes = [] } = useTenantTypes();
  const createMut = useCreateTenant();
  const updateMut = useUpdateTenant();
  const deleteMut = useDeleteTenant();
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const ttByKey = useMemo(() => Object.fromEntries(tenantTypes.map((t) => [t.key, t])), [tenantTypes]);

  const onSave = async (id, body, isNew) => {
    try {
      if (isNew) await createMut.mutateAsync(body);
      else await updateMut.mutateAsync({ id, body });
      addToast({ type: "success", title: isNew ? "Tenant created" : "Tenant updated" });
      setEditing(null);
    } catch (e) {
      addToast({ type: "error", title: "Save failed", body: e?.response?.data?.detail || e.message });
    }
  };
  const onDelete = async (t) => {
    if (!window.confirm(`Delete tenant "${t.name}"?`)) return;
    try {
      await deleteMut.mutateAsync(t.id);
      addToast({ type: "success", title: "Tenant deleted" });
    } catch (e) {
      addToast({ type: "error", title: "Delete failed", body: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div style={mono(10, "var(--dimmer, #9198b0)", 700, "0.8px")} className="uppercase">
          Tenants · {tenants.length}
        </div>
        {isPlatform && (
          <button type="button" onClick={() => setEditing("new")}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
            style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
            data-testid="tenant-new-btn">
            <Plus size={12} /> Add tenant
          </button>
        )}
      </div>

      <div className="space-y-2">
        {tenants.map((t) => (
          <TenantCard
            key={t.id} t={t} ttByKey={ttByKey} tenantTypes={tenantTypes} locale={locale} isPlatform={isPlatform}
            expanded={expanded === t.id}
            onToggleExpand={() => setExpanded(expanded === t.id ? null : t.id)}
            editing={editing === t.id}
            onEdit={() => setEditing(t.id)}
            onCancel={() => setEditing(null)}
            onSave={(body) => onSave(t.id, body, false)}
            onDelete={() => onDelete(t)} />
        ))}
        {editing === "new" && (
          <TenantCard t={null} ttByKey={ttByKey} tenantTypes={tenantTypes} locale={locale} isPlatform={isPlatform}
            editing={true}
            onCancel={() => setEditing(null)}
            onSave={(body) => onSave(null, body, true)} />
        )}
      </div>
    </div>
  );
};

const TenantCard = ({ t, ttByKey, tenantTypes, locale, isPlatform, expanded, onToggleExpand, editing, onEdit, onCancel, onSave, onDelete }) => {
  const [name, setName] = useState(t?.name || "");
  const [typeKey, setTypeKey] = useState(t?.tenant_type_key || "");
  const [layer, setLayer] = useState(t?.layer || "");
  const [plan, setPlan] = useState(t?.plan || "");
  const [initials, setInitials] = useState(t?.initials || "");

  const handleSave = () => {
    if (!name.trim()) return;
    const tt = ttByKey[typeKey];
    onSave({
      name: name.trim(),
      tenant_type_key: typeKey || null,
      layer: layer ? parseInt(layer, 10) : (tt?.layer || null),
      plan: plan.trim() || null,
      initials: initials.trim() || null,
    });
  };

  if (editing) {
    return (
      <div className="rounded-md p-4" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(24,95,165,0.5)" }}
        data-testid={t ? `tenant-edit-${t.id}` : "tenant-edit-new"}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <Label>Name *</Label>
            <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} data-testid="tenant-edit-name" required />
          </div>
          <div>
            <Label>Initials</Label>
            <input value={initials} onChange={(e) => setInitials(e.target.value)} maxLength={3} placeholder="auto" style={inputStyle} data-testid="tenant-edit-initials" />
          </div>
          <div>
            <Label>Tenant type</Label>
            <select value={typeKey} onChange={(e) => {
              setTypeKey(e.target.value);
              const t2 = tenantTypes.find((x) => x.key === e.target.value);
              if (t2) setLayer(String(t2.layer));
            }} style={inputStyle} data-testid="tenant-edit-type">
              <option value="">— select —</option>
              {tenantTypes.map((tt) => <option key={tt.key} value={tt.key}>{pickName(tt, locale)} (L{tt.layer})</option>)}
            </select>
          </div>
          <div>
            <Label>Layer</Label>
            <select value={layer} onChange={(e) => setLayer(e.target.value)} style={inputStyle} data-testid="tenant-edit-layer">
              <option value="">—</option>
              {[1, 2, 3, 4, 5].map((l) => <option key={l} value={l}>L{l}</option>)}
            </select>
          </div>
          <div className="md:col-span-2">
            <Label>Plan</Label>
            <input value={plan} onChange={(e) => setPlan(e.target.value)} placeholder="Enterprise / Pro / Starter" style={inputStyle} data-testid="tenant-edit-plan" />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel}
            className="px-3 py-1.5 rounded"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "var(--text-dim, #c5cad8)", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
            data-testid="tenant-edit-cancel">
            <X size={11} className="inline-block mr-1" /> Cancel
          </button>
          <button type="button" onClick={handleSave}
            className="px-3 py-1.5 rounded"
            style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
            data-testid="tenant-edit-save">
            <Check size={11} className="inline-block mr-1" /> Save
          </button>
        </div>
      </div>
    );
  }

  const tt = ttByKey[t.tenant_type_key];
  return (
    <div className="rounded-md" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }}
      data-testid={`tenant-row-${t.id}`}>
      <div className="p-3 flex items-center justify-between gap-3 flex-wrap cursor-pointer" onClick={onToggleExpand}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="px-1.5 py-0.5 rounded font-bold" style={{ background: "rgba(24,95,165,0.18)", color: "#9ec3ec", ...mono(10, undefined, 700, "0.4px") }}>{t.initials}</span>
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>{t.name}</span>
          {tt && <span style={mono(10, "var(--dimmer, #9198b0)")}>{pickName(tt, locale)} · L{tt.layer}</span>}
          {!tt && t.type && <span style={mono(10, "var(--dimmer, #9198b0)")}>{t.type}</span>}
          {t.plan && <span className="px-1.5 py-0.5 rounded" style={{ background: "rgba(78,208,132,0.10)", color: "#4ed084", ...mono(9, undefined, 700, "0.4px") }}>{t.plan}</span>}
          <span style={mono(9, "var(--dimmer, #9198b0)")}>{(t.contacts || []).length} contact(s)</span>
        </div>
        {isPlatform && (
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={onEdit}
              className="px-2 py-1 rounded inline-flex items-center gap-1"
              style={{ background: "transparent", color: "#9ec3ec", border: "1px solid rgba(24,95,165,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
              data-testid={`tenant-edit-btn-${t.id}`}><Edit2 size={10} /> Edit</button>
            <button type="button" onClick={onDelete}
              className="px-2 py-1 rounded inline-flex items-center gap-1"
              style={{ background: "transparent", color: "#ff9a99", border: "1px solid rgba(226,75,74,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
              data-testid={`tenant-delete-btn-${t.id}`}><Trash2 size={10} /> Del</button>
          </div>
        )}
      </div>
      {expanded && <ContactsEditor tenant={t} isPlatform={isPlatform} />}
    </div>
  );
};

const ContactsEditor = ({ tenant, isPlatform }) => {
  const addToast = useStore((s) => s.addToast);
  const addMut = useAddTenantContact();
  const delMut = useDeleteTenantContact();
  const [form, setForm] = useState({ name: "", role: "", email: "", phone: "" });

  const add = async () => {
    if (!form.name.trim()) return;
    try {
      await addMut.mutateAsync({ tenant_id: tenant.id, body: { ...form, name: form.name.trim() } });
      setForm({ name: "", role: "", email: "", phone: "" });
      addToast({ type: "success", title: "Contact added" });
    } catch (e) {
      addToast({ type: "error", title: "Add failed", body: e?.response?.data?.detail || e.message });
    }
  };
  const remove = async (c) => {
    if (!window.confirm(`Remove contact "${c.name}"?`)) return;
    try {
      await delMut.mutateAsync({ tenant_id: tenant.id, contact_id: c.id });
      addToast({ type: "success", title: "Contact removed" });
    } catch (e) {
      addToast({ type: "error", title: "Remove failed" });
    }
  };

  return (
    <div className="px-4 pb-3 pt-1" style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }} data-testid={`tenant-contacts-${tenant.id}`}>
      <div className="uppercase mt-2 mb-2" style={mono(9, "var(--dimmer, #9198b0)", 700, "0.8px")}>
        <UsersIcon size={10} className="inline-block mr-1" /> Contacts
      </div>
      <div className="space-y-1">
        {(tenant.contacts || []).length === 0 && (
          <div style={mono(11, "var(--dimmer, #9198b0)")}>No contacts yet.</div>
        )}
        {(tenant.contacts || []).map((c) => (
          <div key={c.id} className="flex items-center gap-3 flex-wrap py-1" data-testid={`contact-${c.id}`}>
            <span style={{ ...mono(11, "var(--text, #f0f2f8)"), fontWeight: 700 }}>{c.name}</span>
            {c.role && <span style={mono(10, "var(--text-dim, #c5cad8)")}>· {c.role}</span>}
            {c.email && <span style={mono(10, "#9ec3ec")}><Mail size={9} className="inline mr-0.5" />{c.email}</span>}
            {c.phone && <span style={mono(10, "#9ec3ec")}><Phone size={9} className="inline mr-0.5" />{c.phone}</span>}
            {isPlatform && (
              <button type="button" onClick={() => remove(c)}
                className="ml-auto px-1.5 py-0.5 rounded"
                style={{ background: "transparent", color: "#ff9a99", border: "1px solid rgba(226,75,74,0.30)", ...mono(8, undefined, 700, "0.4px"), cursor: "pointer" }}
                data-testid={`contact-delete-${c.id}`}><Trash2 size={9} /></button>
            )}
          </div>
        ))}
      </div>
      {isPlatform && (
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_1fr_auto] gap-1.5 mt-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name *" style={inputStyle} data-testid={`contact-add-name-${tenant.id}`} />
          <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Role" style={inputStyle} data-testid={`contact-add-role-${tenant.id}`} />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" style={inputStyle} data-testid={`contact-add-email-${tenant.id}`} />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone" style={inputStyle} data-testid={`contact-add-phone-${tenant.id}`} />
          <button type="button" onClick={add}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1"
            style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
            data-testid={`contact-add-btn-${tenant.id}`}><Plus size={11} /> Add</button>
        </div>
      )}
    </div>
  );
};

// =================================================================
// Tab 2 — Relationships
// =================================================================
const RelationshipsTab = ({ isPlatform }) => {
  const addToast = useStore((s) => s.addToast);
  const { data: tenants = [] } = useTenants();
  const { data: rels = [] } = useRelationships();
  const createMut = useCreateRelationship();
  const deleteMut = useDeleteRelationship();
  const tenantsById = useMemo(() => Object.fromEntries(tenants.map((t) => [t.id, t])), [tenants]);

  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ from_tenant_id: "", to_tenant_id: "", relationship_type: "supplies-to", from_contacts: [], to_contacts: [], part_numbers: "" });

  const handleCreate = async () => {
    if (!form.from_tenant_id || !form.to_tenant_id) return;
    const body = {
      ...form,
      part_numbers: form.part_numbers.split(",").map((p) => p.trim()).filter(Boolean),
    };
    try {
      await createMut.mutateAsync(body);
      addToast({ type: "success", title: "Relationship created" });
      setShowNew(false);
      setForm({ from_tenant_id: "", to_tenant_id: "", relationship_type: "supplies-to", from_contacts: [], to_contacts: [], part_numbers: "" });
    } catch (e) {
      addToast({ type: "error", title: "Create failed", body: e?.response?.data?.detail || e.message });
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm("Delete this relationship?")) return;
    try {
      await deleteMut.mutateAsync(r.id);
      addToast({ type: "success", title: "Deleted" });
    } catch (e) {
      addToast({ type: "error", title: "Delete failed" });
    }
  };

  const fromTenant = tenantsById[form.from_tenant_id];
  const toTenant = tenantsById[form.to_tenant_id];

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div style={mono(10, "var(--dimmer, #9198b0)", 700, "0.8px")} className="uppercase">
          Relationships · {rels.length}
        </div>
        {isPlatform && (
          <button type="button" onClick={() => setShowNew(true)}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
            style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
            data-testid="relationship-new-btn">
            <Plus size={12} /> Add link
          </button>
        )}
      </div>

      {showNew && (
        <div className="rounded-md p-4 mb-3" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(24,95,165,0.5)" }} data-testid="relationship-new-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <Label>From tenant *</Label>
              <select value={form.from_tenant_id} onChange={(e) => setForm({ ...form, from_tenant_id: e.target.value, from_contacts: [] })} style={inputStyle} data-testid="rel-from-tenant">
                <option value="">— select —</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Relationship</Label>
              <select value={form.relationship_type} onChange={(e) => setForm({ ...form, relationship_type: e.target.value })} style={inputStyle} data-testid="rel-type">
                {RELATIONSHIP_TYPES.map((rt) => <option key={rt} value={rt}>{rt}</option>)}
              </select>
            </div>
            <div>
              <Label>To tenant *</Label>
              <select value={form.to_tenant_id} onChange={(e) => setForm({ ...form, to_tenant_id: e.target.value, to_contacts: [] })} style={inputStyle} data-testid="rel-to-tenant">
                <option value="">— select —</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <Label>From contacts</Label>
              <select multiple value={form.from_contacts} onChange={(e) => setForm({ ...form, from_contacts: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                style={{ ...inputStyle, height: 70 }} data-testid="rel-from-contacts">
                {(fromTenant?.contacts || []).map((c) => <option key={c.id} value={c.id}>{c.name} {c.role ? `· ${c.role}` : ""}</option>)}
              </select>
            </div>
            <div>
              <Label>Part numbers (comma-separated)</Label>
              <input value={form.part_numbers} onChange={(e) => setForm({ ...form, part_numbers: e.target.value })} placeholder="PN-001, PN-002" style={inputStyle} data-testid="rel-parts" />
            </div>
            <div>
              <Label>To contacts</Label>
              <select multiple value={form.to_contacts} onChange={(e) => setForm({ ...form, to_contacts: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                style={{ ...inputStyle, height: 70 }} data-testid="rel-to-contacts">
                {(toTenant?.contacts || []).map((c) => <option key={c.id} value={c.id}>{c.name} {c.role ? `· ${c.role}` : ""}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowNew(false)} className="px-3 py-1.5 rounded"
              style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "var(--text-dim, #c5cad8)", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
              data-testid="rel-cancel">Cancel</button>
            <button type="button" onClick={handleCreate} className="px-3 py-1.5 rounded"
              style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
              data-testid="rel-save">Create</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {rels.length === 0 && <div style={mono(11, "var(--dimmer, #9198b0)")}>No relationships defined yet.</div>}
        {rels.map((r) => {
          const fromT = tenantsById[r.from_tenant_id];
          const toT = tenantsById[r.to_tenant_id];
          const fromContactNames = (fromT?.contacts || []).filter((c) => r.from_contacts.includes(c.id)).map((c) => c.name);
          const toContactNames = (toT?.contacts || []).filter((c) => r.to_contacts.includes(c.id)).map((c) => c.name);
          return (
            <div key={r.id} className="rounded-md p-3" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid={`rel-row-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span style={{ ...mono(11, "var(--text, #f0f2f8)"), fontWeight: 700 }}>{fromT?.name || r.from_tenant_id}</span>
                <span className="px-2 py-0.5 rounded" style={{ background: "rgba(24,95,165,0.18)", color: "#9ec3ec", ...mono(9, undefined, 700, "0.4px") }}>{r.relationship_type}</span>
                <ArrowRight size={11} color="#9198b0" />
                <span style={{ ...mono(11, "var(--text, #f0f2f8)"), fontWeight: 700 }}>{toT?.name || r.to_tenant_id}</span>
                {isPlatform && (
                  <button type="button" onClick={() => handleDelete(r)}
                    className="ml-auto px-1.5 py-0.5 rounded"
                    style={{ background: "transparent", color: "#ff9a99", border: "1px solid rgba(226,75,74,0.30)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
                    data-testid={`rel-delete-${r.id}`}><Trash2 size={10} /></button>
                )}
              </div>
              <div className="flex flex-wrap gap-3" style={mono(10, "var(--text-dim, #c5cad8)")}>
                {fromContactNames.length > 0 && <span>From contacts: <b style={{ color: "#9ec3ec" }}>{fromContactNames.join(", ")}</b></span>}
                {toContactNames.length > 0 && <span>To contacts: <b style={{ color: "#9ec3ec" }}>{toContactNames.join(", ")}</b></span>}
                {r.part_numbers?.length > 0 && <span>Parts: <b style={{ color: "#f3b14a" }}>{r.part_numbers.join(", ")}</b></span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// =================================================================
// Tab 3 — Parts
// =================================================================
const PartsTab = ({ isPlatform }) => {
  const addToast = useStore((s) => s.addToast);
  const { data: tenants = [] } = useTenants();
  const tenantsById = useMemo(() => Object.fromEntries(tenants.map((t) => [t.id, t])), [tenants]);
  const [parts, setParts] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const upsertMut = useUpsertPart();
  const updateMut = useUpdatePart();
  const deleteMut = useDeletePart();

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/parts${search ? `?q=${encodeURIComponent(search)}` : ""}`;
      const resp = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem("sitroom_token") || ""}` },
      });
      if (!cancelled && resp.ok) setParts(await resp.json());
    })();
    return () => { cancelled = true; };
  }, [search, upsertMut.isSuccess, updateMut.isSuccess, deleteMut.isSuccess]);

  const refreshParts = async () => {
    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/parts${search ? `?q=${encodeURIComponent(search)}` : ""}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("sitroom_token") || ""}` },
    });
    if (resp.ok) setParts(await resp.json());
  };

  const onSave = async (originalPn, body, isNew) => {
    try {
      if (isNew) await upsertMut.mutateAsync(body);
      else await updateMut.mutateAsync({ pn: originalPn, body });
      addToast({ type: "success", title: isNew ? "Part created" : "Part updated" });
      setEditing(null);
      await refreshParts();
    } catch (e) {
      addToast({ type: "error", title: "Save failed", body: e?.response?.data?.detail || e.message });
    }
  };
  const onDelete = async (p) => {
    if (!window.confirm(`Delete part "${p.part_number}"?`)) return;
    try {
      await deleteMut.mutateAsync(p.part_number);
      addToast({ type: "success", title: "Part deleted" });
      await refreshParts();
    } catch (e) {
      addToast({ type: "error", title: "Delete failed", body: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3 flex-wrap">
          <div style={mono(10, "var(--dimmer, #9198b0)", 700, "0.8px")} className="uppercase">Parts · {parts.length}</div>
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search part number / name" style={{ ...inputStyle, width: 280 }} data-testid="parts-search" />
        </div>
        {isPlatform && (
          <button type="button" onClick={() => setEditing("new")}
            className="px-3 py-1.5 rounded inline-flex items-center gap-1.5"
            style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
            data-testid="part-new-btn">
            <Plus size={12} /> Add part
          </button>
        )}
      </div>

      {editing === "new" && (
        <PartEditor part={null} tenants={tenants} tenantsById={tenantsById}
          onCancel={() => setEditing(null)}
          onSave={(body) => onSave(null, body, true)} />
      )}

      <div className="space-y-2">
        {parts.map((p) => (
          <PartRow key={p.part_number} p={p} tenants={tenants} tenantsById={tenantsById} isPlatform={isPlatform}
            editing={editing === p.part_number}
            onEdit={() => setEditing(p.part_number)}
            onCancel={() => setEditing(null)}
            onSave={(body) => onSave(p.part_number, body, false)}
            onDelete={() => onDelete(p)} />
        ))}
      </div>
    </div>
  );
};

const PartRow = ({ p, tenants, tenantsById, isPlatform, editing, onEdit, onCancel, onSave, onDelete }) => {
  if (editing) {
    return <PartEditor part={p} tenants={tenants} tenantsById={tenantsById} onCancel={onCancel} onSave={onSave} />;
  }
  const owner = p.owning_tenant_id ? tenantsById[p.owning_tenant_id] : null;
  const respNames = [];
  if (owner && p.responsible_contacts?.length) {
    for (const t of tenants) {
      for (const c of (t.contacts || [])) {
        if (p.responsible_contacts.includes(c.id)) respNames.push(`${c.name} (${t.name})`);
      }
    }
  }
  return (
    <div className="rounded-md p-3" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid={`part-row-${p.part_number}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Package size={13} color="#9ec3ec" />
        <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text, #f0f2f8)" }}>{p.part_number}</span>
        {p.name && <span style={mono(11, "var(--text-dim, #c5cad8)")}>· {p.name}</span>}
        {p.category && <span className="px-1.5 py-0.5 rounded" style={{ background: "rgba(78,208,132,0.10)", color: "#4ed084", ...mono(9, undefined, 700, "0.4px") }}>{p.category}</span>}
        {owner && <span style={mono(10, "#9ec3ec")}>Owner: {owner.name}</span>}
        {isPlatform && (
          <div className="ml-auto flex gap-1">
            <button type="button" onClick={onEdit} className="px-2 py-1 rounded inline-flex items-center gap-1"
              style={{ background: "transparent", color: "#9ec3ec", border: "1px solid rgba(24,95,165,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
              data-testid={`part-edit-${p.part_number}`}><Edit2 size={10} /> Edit</button>
            <button type="button" onClick={onDelete} className="px-2 py-1 rounded inline-flex items-center gap-1"
              style={{ background: "transparent", color: "#ff9a99", border: "1px solid rgba(226,75,74,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
              data-testid={`part-delete-${p.part_number}`}><Trash2 size={10} /> Del</button>
          </div>
        )}
      </div>
      {respNames.length > 0 && (
        <div className="mt-1.5" style={mono(10, "var(--text-dim, #c5cad8)")}>
          Responsible: <b style={{ color: "#f3b14a" }}>{respNames.join(", ")}</b>
        </div>
      )}
    </div>
  );
};

const PartEditor = ({ part, tenants, tenantsById, onCancel, onSave }) => {
  const [pn, setPn] = useState(part?.part_number || "");
  const [description, setDescription] = useState(part?.description || "");
  const [name, setName] = useState(part?.name || "");
  const [category, setCategory] = useState(part?.category || "");
  const [supplier, setSupplier] = useState(part?.supplier || "");
  const [owningTenantId, setOwningTenantId] = useState(part?.owning_tenant_id || "");
  const [respContacts, setRespContacts] = useState(part?.responsible_contacts || []);

  const allContacts = useMemo(() => {
    const out = [];
    for (const t of tenants) {
      for (const c of (t.contacts || [])) out.push({ ...c, tenant_name: t.name, tenant_id: t.id });
    }
    return out;
  }, [tenants]);

  const handleSave = () => {
    if (!pn.trim()) return;
    const body = {
      description: description.trim() || null,
      name: name.trim() || null,
      category: category.trim() || null,
      supplier: supplier.trim() || null,
      owning_tenant_id: owningTenantId || null,
      responsible_contacts: respContacts,
    };
    if (!part) body.part_number = pn.trim();
    onSave(body);
  };

  return (
    <div className="rounded-md p-4 mb-2" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(24,95,165,0.5)" }} data-testid={part ? `part-edit-form-${part.part_number}` : "part-edit-form-new"}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <Label>Part number *</Label>
          <input value={pn} onChange={(e) => setPn(e.target.value)} disabled={!!part} style={{ ...inputStyle, opacity: part ? 0.5 : 1 }} data-testid="part-edit-pn" required />
        </div>
        <div>
          <Label>Display name</Label>
          <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} data-testid="part-edit-name" />
        </div>
        <div className="md:col-span-2">
          <Label>Description</Label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} style={inputStyle} data-testid="part-edit-description" />
        </div>
        <div>
          <Label>Category</Label>
          <input value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle} data-testid="part-edit-category" />
        </div>
        <div>
          <Label>Supplier (legacy free-text)</Label>
          <input value={supplier} onChange={(e) => setSupplier(e.target.value)} style={inputStyle} data-testid="part-edit-supplier" />
        </div>
        <div>
          <Label>Owning tenant</Label>
          <select value={owningTenantId} onChange={(e) => setOwningTenantId(e.target.value)} style={inputStyle} data-testid="part-edit-owner">
            <option value="">— none —</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <Label>Responsible contacts</Label>
          <select multiple value={respContacts} onChange={(e) => setRespContacts(Array.from(e.target.selectedOptions).map((o) => o.value))}
            style={{ ...inputStyle, height: 80 }} data-testid="part-edit-contacts">
            {allContacts.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.tenant_name}</option>)}
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded"
          style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "var(--text-dim, #c5cad8)", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
          data-testid="part-edit-cancel">Cancel</button>
        <button type="button" onClick={handleSave} className="px-3 py-1.5 rounded"
          style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
          data-testid="part-edit-save">Save</button>
      </div>
    </div>
  );
};
